
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { WEBGL } from 'three/examples/jsm/WebGL.js'
import ThreeDRenderer, { load3DFile, addModelToScene, addLights } from '../services/three-d-render.service'
import ResizeObserver from 'resize-observer-polyfill'
import axios from 'axios'

export default defineComponent({
  name: 'ThreeDViewer',
  props: {
    src: {
      type: String,
      required: true
    },
    interactive: {
      type: Boolean,
      default: false
    },
    height: String,
    width: String
  },
  setup(props) {
    const renderRequested = ref(false)
    const webGlError = ref(false)
    const otherError = ref('')
    const scene = ref<THREE.Scene>()
    const camera = ref<THREE.PerspectiveCamera>()
    const renderer = ref<THREE.WebGLRenderer>()
    const controls = ref<OrbitControls>()
    const ro = ref<ResizeObserver>()
    const renderedImg = ref<HTMLImageElement>()
    const canvas = ref<HTMLCanvasElement>()

    onMounted(async () => {
      if (await isTooBig()) {
        otherError.value = 'File is too large to preview'
        return
      }

      // Make sure the user's browser supports WebGL
      if (WEBGL.isWebGLAvailable()) {
        if (props.interactive) {
          initializeImage()
        } else {
          ro.value = new ResizeObserver(renderModel)
          ro.value.observe(renderedImg.value as HTMLImageElement)
          renderModel()
        }
      } else {
        webGlError.value = true
      }
    })

    onBeforeUnmount(() => {
      if (ro.value === null || ro.value === undefined) return
      ro.value.unobserve(renderedImg.value as HTMLImageElement)
    })

    watch(
      () => props.src,
      async () => {
        if (await isTooBig()) {
          otherError.value = 'File is too large to preview'
          return
        }
        props.interactive ? updateScene() : renderModel()
      }
    )

    async function renderModel() {
      if (!renderedImg.value) return

      const height = renderedImg.value.offsetHeight
      const width = renderedImg.value.offsetWidth

      // I wasn't able to find a way to set the components size while testing
      // so we ignore this check in tests
      if ((height <= 0 || width <= 0) && process.env.NODE_ENV !== 'test') return
      ThreeDRenderer.renderModel(props.src, height, width, image => {
        if (image && renderedImg.value) {
          renderedImg.value.src = image
        }
      })
    }

    async function isTooBig() {
      // Here we're checking the size of a 3D file so we can skip rendering files
      // that are so big they will block the render thread for a significant amount
      // of time
      const result = await axios.head(props.src)
      if (parseInt(result.headers['content-length']) < 5242880 /*5 MB*/) return false
      return true
    }

    async function initializeImage() {
      // Setup the WebGL renderer and add it to the DOM.
      let height = 0
      let width = 0
      if (canvas.value) {
        renderer.value = new THREE.WebGLRenderer({ canvas: canvas.value })
        height = canvas.value.offsetHeight
        width = canvas.value.offsetWidth
        renderer.value.setSize(width, height)
      } else {
        otherError.value = 'Unable to render 3D preview'
        return
      }
      // Set up the THREE scene and camera.
      const localScene = new THREE.Scene()
      localScene.background = new THREE.Color(0xffffff)
      const localCamera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
      localCamera.position.z = 100
      camera.value = localCamera
      scene.value = localScene
      const modelRes = await load3DFile(props.src)
      // Stop trying to create the viewer if there is a loading error
      if (modelRes.error) {
        otherError.value = modelRes.error
        return
      }

      if (!modelRes.model) {
        otherError.value = 'Could not load model preview'
        return
      }

      addModelToScene(modelRes.model, scene.value)
      addLights(scene.value)
      // Setup our mouse controls to allow for panning/zooming.
      if (props.interactive) {
        controls.value = new OrbitControls(camera.value, renderer.value.domElement)
        controls.value.update()
        controls.value.addEventListener('change', requestRenderIfNotRequested)
      }
      render()
    }

    async function updateScene() {
      const modelRes = await load3DFile(props.src)
      // Stop trying to create the viewer if there is a loading error
      if (modelRes.error) {
        otherError.value = modelRes.error
        return
      }

      if (!modelRes.model) {
        otherError.value = 'Could not load model preview'
        return
      }

      if (scene.value) {
        addModelToScene(modelRes.model, scene.value)
        addLights(scene.value)
      }

      render()
    }

    function requestRenderIfNotRequested() {
      if (!renderRequested.value) {
        renderRequested.value = true
        requestAnimationFrame(render)
      }
    }

    function render() {
      renderRequested.value = false
      if (renderer.value && scene.value && camera.value) {
        renderer.value.render(scene.value, camera.value)
      }
    }

    return {
      webGlError,
      otherError,
      renderedImg,
      canvas
    }
  }
})
